/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { Box, InputBase, Stack, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { Text } from "@src/appV2/redesign/components/Text";
import { addDays, isEqual } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

import { type AmPm, AmPmToggle } from "./AmPmToggle";
import { DateToggle } from "./DateToggle";
import {
  formatTimeString,
  getBorderColor,
  getDayOptions,
  getSelectedDay,
  parseTimeString,
  sanitizeTimeInput,
  to12Hour,
  to24Hour,
  updateDateInTimezone,
} from "./utils";

export interface BerlinTimePickerProps {
  value: Date;
  onChange: (value: Date) => void;
  title?: string;
  timezone?: string;
  startDate?: Date;
  endDate?: Date;
  hasError?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * A Berlin-style time picker with an embedded AM/PM toggle.
 *
 * Features:
 * - Time input with h:mm format
 * - Embedded pill-style AM/PM toggle positioned at bottom-right
 * - Timezone support
 * - Keyboard navigation
 * - Error state styling
 */
export function BerlinTimePicker(props: BerlinTimePickerProps) {
  const {
    value,
    onChange,
    title,
    timezone,
    startDate,
    endDate,
    hasError = false,
    ariaLabel,
    placeholder = "0:00",
    disabled = false,
    sx,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Convert UTC value to zoned time if timezone is provided
  const zonedValue = useMemo(
    () => (isDefined(timezone) ? utcToZonedTime(value, timezone) : value),
    [value, timezone]
  );

  const dayOptions = useMemo(
    () => getDayOptions(startDate, endDate, timezone),
    [startDate, endDate, timezone]
  );

  const selectedDate = useMemo(
    () => (isDefined(dayOptions) ? getSelectedDay(value, timezone) : undefined),
    [dayOptions, timezone, value]
  );

  const previousDate = useMemo(() => {
    if (!isDefined(dayOptions) || !isDefined(selectedDate)) {
      return undefined;
    }
    if (isEqual(selectedDate, dayOptions.minDay)) {
      return undefined;
    }
    return addDays(selectedDate, -1);
  }, [dayOptions, selectedDate]);

  const nextDate = useMemo(() => {
    if (!isDefined(dayOptions) || !isDefined(selectedDate)) {
      return undefined;
    }
    if (isEqual(selectedDate, dayOptions.maxDay)) {
      return undefined;
    }
    return addDays(selectedDate, 1);
  }, [dayOptions, selectedDate]);

  const { hours12, amPm: initialAmPm } = useMemo(
    () => to12Hour(zonedValue.getHours()),
    [zonedValue]
  );

  const [inputValue, setInputValue] = useState(() =>
    formatTimeString(hours12, zonedValue.getMinutes())
  );
  const [amPm, setAmPm] = useState<AmPm>(initialAmPm);

  // Sync input value when the external value changes, but skip while focused to avoid
  // resetting what the user is typing. Clamping and normalization still apply on blur.
  useEffect(() => {
    if (isFocused) {
      return;
    }

    const { hours12: newHours12, amPm: newAmPm } = to12Hour(zonedValue.getHours());
    setInputValue(formatTimeString(newHours12, zonedValue.getMinutes()));
    setAmPm(newAmPm);
  }, [zonedValue, isFocused]);

  const updateValue = useCallback(
    (hours12Value: number, minutesValue: number, amPmValue: AmPm) => {
      const hours24 = to24Hour(hours12Value, amPmValue);
      const newDate = new Date(zonedValue);
      newDate.setHours(hours24, minutesValue, 0, 0);

      const finalValue = isDefined(timezone) ? zonedTimeToUtc(newDate, timezone) : newDate;
      onChange(finalValue);
    },
    [zonedValue, timezone, onChange]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const rawCaret = event.target.selectionStart ?? raw.length;
      const { value: sanitized, caret } = sanitizeTimeInput(raw, rawCaret);

      flushSync(() => {
        setInputValue(sanitized);
      });

      inputRef.current?.setSelectionRange(caret, caret);

      // Fire onChange immediately when the input is already a valid time so that
      // dependent state (e.g. a Save button's disabled flag) updates without waiting for blur.
      const parsed = parseTimeString(sanitized);
      if (parsed) {
        const clampedHours = Math.min(Math.max(parsed.hours, 1), 12);
        updateValue(clampedHours, parsed.minutes, amPm);
      }
    },
    [amPm, updateValue]
  );

  const handleInputBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      const parsed = parseTimeString(event.target.value);
      if (parsed) {
        // Clamp hours to 1-12 for 12-hour format
        const clampedHours = Math.min(Math.max(parsed.hours, 1), 12);
        updateValue(clampedHours, parsed.minutes, amPm);
      } else {
        // Reset to current value if invalid
        setInputValue(formatTimeString(hours12, zonedValue.getMinutes()));
      }
    },
    [amPm, hours12, zonedValue, updateValue]
  );

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        const parsed = parseTimeString(inputValue);
        if (parsed) {
          const clampedHours = Math.min(Math.max(parsed.hours, 1), 12);
          updateValue(clampedHours, parsed.minutes, amPm);
        }
        inputRef.current?.blur();
      }
    },
    [inputValue, amPm, updateValue]
  );

  const handleAmPmChange = useCallback(
    (newAmPm: AmPm) => {
      setAmPm(newAmPm);
      const parsed = parseTimeString(inputValue);
      if (parsed) {
        const clampedHours = Math.min(Math.max(parsed.hours, 1), 12);
        updateValue(clampedHours, parsed.minutes, newAmPm);
      } else {
        updateValue(hours12, zonedValue.getMinutes(), newAmPm);
      }
    },
    [inputValue, hours12, zonedValue, updateValue]
  );

  const updateDate = useCallback(
    (newDate: Date) => onChange(updateDateInTimezone(value, newDate, timezone)),
    [onChange, timezone, value]
  );

  const onSelectPreviousDate = useCallback(() => {
    if (isDefined(previousDate)) {
      updateDate(previousDate);
    }
  }, [previousDate, updateDate]);

  const onSelectNextDate = useCallback(() => {
    if (isDefined(nextDate)) {
      updateDate(nextDate);
    }
  }, [nextDate, updateDate]);

  return (
    <Stack
      spacing={1}
      sx={mergeSxProps(
        {
          width: 160,
        },
        sx
      )}
    >
      {(isDefined(title) || isDefined(dayOptions)) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {isDefined(title) && (
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              {title}
            </Text>
          )}
          {isDefined(dayOptions) && (
            <DateToggle
              selectedDate={selectedDate ?? dayOptions.minDay}
              previousDate={previousDate}
              nextDate={nextDate}
              timezone={timezone}
              disabled={disabled}
              onSelectPreviousDate={onSelectPreviousDate}
              onSelectNextDate={onSelectNextDate}
            />
          )}
        </Stack>
      )}
      <Box
        sx={(theme: Theme) => ({
          position: "relative",
          display: "flex",
          alignItems: "center",
          padding: "10px",
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${getBorderColor({ hasError, isFocused, theme })}`,
          borderRadius: theme.borderRadius?.large,
          width: "100%",
          minHeight: 44,
          boxSizing: "border-box",
          transition: theme.transitions.create(["border-color", "box-shadow"], {
            duration: theme.transitions.duration.short,
          }),
          ...(isFocused && {
            boxShadow: `0px 0px 0px 4px ${theme.palette.primary.main}14`,
          }),
          "&:hover": {
            borderColor: getBorderColor({ hasError, isFocused, theme, isHover: true }),
          },
        })}
      >
        <InputBase
          aria-label={ariaLabel}
          disabled={disabled}
          inputRef={inputRef}
          placeholder={placeholder}
          value={inputValue}
          inputProps={{
            inputMode: "numeric",
            maxLength: 5,
            style: {
              padding: 0,
            },
          }}
          sx={(theme) => ({
            flex: 1,
            fontSize: theme.typography.body2.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.palette.text.primary,
            // iOS auto-zooms inputs rendered below 16px on focus; keep touch devices at 16px.
            "@media (pointer: coarse)": {
              fontSize: "16px",
            },
            "& .MuiInputBase-input": {
              padding: 0,
              "&::placeholder": {
                color: theme.palette.text.disabled,
                opacity: 1,
              },
            },
          })}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
        />
        <Box
          sx={{
            position: "absolute",
            right: 7,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <AmPmToggle disabled={disabled} value={amPm} onChange={handleAmPmChange} />
        </Box>
      </Box>
    </Stack>
  );
}
/* eslint-enable max-lines */
